import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  Activity,
  Ban,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  CircleDot,
  Clock3,
  Pause,
  Play,
  Power,
  Rocket,
  ScanLine,
  Square,
  type LucideIcon,
} from "lucide-react";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FieldSelectFullWidth } from "@/components/trade-filter-selects";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { listDeployEvents } from "../../../services/strategyDeployApi";
import { useStrategyDeployLive } from "../../../hooks/useStrategyDeployLive";
import type {
  DeployEvent,
  DeployLiveMessage,
  Deployment,
} from "../../../types/strategyDeploy";
import { GREEN } from "./liveStrategiesFormat";
import {
  liveCardBodyClassName,
  liveCardClassName,
  liveLabelClassName,
  liveSectionTitleClassName,
  liveSmallTextClassName,
} from "./liveStrategiesUi";
import { LiveBadge, type LiveTone } from "./LiveUiParts";

const ALL_STRATEGIES = "all";

type Filter = "all" | "checks" | "trades" | "problems";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "checks", label: "Checks" },
  { id: "trades", label: "Signals" },
  { id: "problems", label: "Problems" },
];

const TRADE_KINDS = new Set([
  "signal",
  "approved",
  "rejected",
  "expired",
  "filled",
  "closed",
  "skipped",
  "alert",
  "killed",
  "reconciled",
]);
const PROBLEM_KINDS = new Set(["error", "failed", "waiting"]);

function kindOf(e: DeployEvent): Filter {
  if (e.kind === "check") return "checks";
  if (PROBLEM_KINDS.has(e.kind)) return "problems";
  if (TRADE_KINDS.has(e.kind)) return "trades";
  return "all";
}

const KIND_LABEL: Record<string, string> = {
  check: "Check",
  signal: "Signal",
  approved: "Approved",
  rejected: "Rejected",
  expired: "Expired",
  filled: "Filled",
  closed: "Closed",
  skipped: "Skipped",
  alert: "Alert",
  killed: "Killed",
  kill: "Killed",
  reconciled: "Reconciled",
  error: "Error",
  failed: "Failed",
  waiting: "Waiting",
  pause: "Paused",
  resume: "Resumed",
  stop: "Stopped",
  created: "Launched",
};

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? (kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : "Event");
}

function tone(e: DeployEvent): { tone: LiveTone; Icon: LucideIcon } {
  if (PROBLEM_KINDS.has(e.kind)) return { tone: "bad", Icon: AlertTriangle };
  if (e.kind === "check") {
    const signals = Number((e.data ?? {}).signals ?? 0);
    return signals ? { tone: "brand", Icon: ScanLine } : { tone: "neutral", Icon: CheckCircle2 };
  }
  if (e.kind === "filled") return { tone: "good", Icon: CircleDot };
  if (e.kind === "rejected" || e.kind === "expired" || e.kind === "skipped") return { tone: "neutral", Icon: Ban };
  if (e.kind === "closed") return { tone: "brand", Icon: CircleDot };
  if (e.kind === "reconciled") return { tone: "brand", Icon: CheckCircle2 };
  if (e.kind === "signal") {
    return /short/i.test(e.message) ? { tone: "bad", Icon: ArrowDownRight } : { tone: "good", Icon: ArrowUpRight };
  }
  if (e.kind === "approved") return { tone: "good", Icon: CheckCircle2 };
  if (e.kind === "pause") return { tone: "warn", Icon: Pause };
  if (e.kind === "resume") return { tone: "good", Icon: Play };
  if (e.kind === "stop") return { tone: "neutral", Icon: Square };
  if (e.kind === "killed" || e.kind === "kill") return { tone: "bad", Icon: Power };
  if (e.kind === "created") return { tone: "brand", Icon: Rocket };
  return { tone: "brand", Icon: Activity };
}

/** Local time the entry happened (a candle check runs right after the candle closes). */
function clock(e: DeployEvent): string {
  const d = new Date(e.created_at);
  if (!Number.isFinite(d.getTime())) return "N/A";
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Which day: "Today", "Yesterday", else "3 Nov" (the log reaches back a few days). */
function day(e: DeployEvent): string {
  const d = new Date(e.created_at);
  if (!Number.isFinite(d.getTime())) return "";
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(new Date()) - start(d)) / 86_400_000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** Problems work like notifications: the count is what's new since the Problems tab was last opened. */
const PROBLEMS_SEEN_KEY = "launchpad.liveLog.problemsSeenAt";
function readProblemsSeenAt(): number {
  try {
    return Number(window.localStorage.getItem(PROBLEMS_SEEN_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** The check line without the UTC time prefix (the row shows local time). */
function text(e: DeployEvent): string {
  // Entries saved before the messages were reworded can still carry dashes.
  const message = e.message.replace(/\s*\u2014\s*/g, ": ");
  return e.kind === "check" ? message.replace(/^\d{2}:\d{2} UTC candle · /, "") : message;
}

/** "FVG Bear · XAUUSD · 15m · policy v1": brand name plus muted market / version. */
function splitName(name: string): [string, string] {
  const i = name.indexOf(" · ");
  return i > 0 ? [name.slice(0, i), name.slice(i + 3)] : [name, ""];
}

type Props = {
  deployments: Deployment[];
  /** Only this deployment's log (detail panel). */
  deploymentId?: string;
  /** Pre-loaded entries for one deployment (detail panel). */
  initial?: DeployEvent[];
  className?: string;
  maxHeight?: number;
  /** Clicking an entry opens its strategy (the page's detail dialog). */
  onOpenEvent?: (e: DeployEvent) => void;
};

/** Everything the strategies did, as it happens: every candle checked, every signal, fill, close and
 *  problem, so it is always visible that they are working. Filter by event type and by strategy. */
export function LiveLog({
  deployments,
  deploymentId,
  initial,
  className,
  maxHeight = 420,
  onOpenEvent,
}: Props): JSX.Element {
  const [problemsSeenAt, setProblemsSeenAt] = useState<number>(() => (deploymentId ? 0 : readProblemsSeenAt()));
  const [events, setEvents] = useState<DeployEvent[] | null>(initial ?? null);
  const [filter, setFilter] = useState<Filter>("all");
  const [strategy, setStrategy] = useState<string>(ALL_STRATEGIES);
  // A strategy that leaves the list (deleted) drops the filter back to every strategy.
  useEffect(() => {
    if (strategy !== ALL_STRATEGIES && !deployments.some((d) => d.id === strategy)) setStrategy(ALL_STRATEGIES);
  }, [deployments, strategy]);
  const strategyOptions = useMemo(
    () => [
      { value: ALL_STRATEGIES, label: "All strategies" },
      ...deployments.map((d) => ({ value: d.id, label: d.name })),
    ],
    [deployments]
  );
  const names = useMemo(() => new Map(deployments.map((d) => [d.id, d.name])), [
    deployments,
  ]);

  const load = useCallback(async () => {
    if (deploymentId) return;
    try {
      setEvents(await listDeployEvents({ limit: 150 }));
    } catch {
      setEvents((prev) => prev ?? []);
    }
  }, [deploymentId]);

  useEffect(() => {
    if (initial) setEvents(initial);
  }, [initial]);
  useEffect(() => {
    void load();
  }, [load]);

  useStrategyDeployLive(
    useCallback(
      (msg: DeployLiveMessage) => {
        const ev = msg.data?.event;
        if (!ev || (deploymentId && ev.deployment_id !== deploymentId)) return;
        setEvents((prev) =>
          prev?.some((x) => x.id === ev.id)
            ? prev
            : [ev, ...(prev ?? [])].slice(0, 300)
        );
      },
      [deploymentId]
    )
  );

  const byStrategy = (events ?? []).filter(
    (e) => deploymentId || strategy === ALL_STRATEGIES || e.deployment_id === strategy
  );
  const shown = byStrategy.filter((e) => filter === "all" || kindOf(e) === filter);
  // Unseen problems only (all of them inside a single strategy's dialog).
  const problems = byStrategy.filter(
    (e) => PROBLEM_KINDS.has(e.kind) && (deploymentId || new Date(e.created_at).getTime() > problemsSeenAt)
  ).length;
  // Opening the Problems tab marks every problem so far as seen: the badge goes back to 0.
  useEffect(() => {
    if (filter !== "problems" || deploymentId) return;
    const latest = byStrategy.reduce((m, e) => Math.max(m, new Date(e.created_at).getTime() || 0), Date.now());
    setProblemsSeenAt(latest);
    try {
      window.localStorage.setItem(PROBLEMS_SEEN_KEY, String(latest));
    } catch {
      // Storage blocked: seen for this visit only.
    }
    // Only on switching to the tab (and on new entries while it is open).
  }, [filter, events, deploymentId]);
  const showStrategy = !deploymentId && strategy === ALL_STRATEGIES;

  return (
    // One card like the strategy groups beside it: title, filters, then the log.
    <section className={cn(liveCardClassName, liveCardBodyClassName, "min-w-0", className)} data-live-log="">
        <div className="flex items-center gap-2">
          <span className="relative flex size-2">
            <span className="absolute inline-flex size-full animate-ping rounded-full opacity-60" style={{ background: GREEN }} />
            <span className="relative inline-flex size-2 rounded-full" style={{ background: GREEN }} />
          </span>
          <h2 className={liveSectionTitleClassName}>Live log</h2>
        </div>
        <Tabs data-trade-log-tabs="" value={filter} onValueChange={(v) => setFilter(v as Filter)} className="w-full">
          <TabsList className="grid w-full grid-cols-4" data-live-log-toolbar="">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.id} value={f.id} data-live-log-filter={f.id} className="gap-1.5">
                {f.label}
                {f.id === "problems" && problems ? (
                  <LiveBadge tone="bad" className="h-4 px-1.5 text-[10px]">
                    {problems}
                  </LiveBadge>
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {!deploymentId ? (
          <div className="min-w-0" data-live-log-strategy-filter="">
            <FieldSelectFullWidth
              value={strategy}
              onValueChange={(v) => setStrategy(v || ALL_STRATEGIES)}
              options={strategyOptions}
            />
          </div>
        ) : null}

        <div
          className="overflow-y-auto rounded-[6px] !border !border-solid !border-border"
          style={{ maxHeight }}
        >
          <div
            className={cn(
              liveLabelClassName,
              "sticky top-0 z-[1] grid grid-cols-[4.5rem_6.5rem_minmax(0,1fr)] items-center gap-3 bg-muted px-4 py-2.5"
            )}
            data-live-log-head=""
          >
            <span>Time</span>
            <span>Event</span>
            <span>{showStrategy ? "Strategy and details" : "Details"}</span>
          </div>
          {events === null ? (
            <div className="flex flex-col gap-2 p-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-4 animate-pulse rounded bg-muted" style={{ width: `${80 - i * 12}%` }} />
              ))}
            </div>
          ) : shown.length ? (
            <ol className="!m-0 !list-none !p-0">
              <AnimatePresence initial={false}>
                {shown.map((e) => {
                  const { tone: t, Icon } = tone(e);
                  const quiet = e.kind === "check" && !Number((e.data ?? {}).signals ?? 0);
                  const [head, tail] = splitName(names.get(e.deployment_id) ?? "Strategy");
                  return (
                    <motion.li
                      key={e.id}
                      layout="position"
                      initial={{
                        opacity: 0,
                        y: -6,
                        backgroundColor: "color-mix(in oklab, var(--brand-tropical-surf) 14%, transparent)",
                      }}
                      animate={{ opacity: 1, y: 0, backgroundColor: "rgba(0,0,0,0)" }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                      className={cn(
                        "grid grid-cols-[4.5rem_6.5rem_minmax(0,1fr)] items-center gap-3 px-4 !border-0 !border-t !border-solid !border-border",
                        quiet ? "py-2" : "py-3",
                        onOpenEvent && "cursor-pointer transition-colors hover:!bg-[color:var(--brand-tropical-surf)]/[0.06]"
                      )}
                      title={new Date(e.created_at).toLocaleString()}
                      data-live-log-kind={e.kind}
                      {...(onOpenEvent
                        ? {
                            role: "button",
                            tabIndex: 0,
                            "aria-label": `Open ${head}`,
                            onClick: () => onOpenEvent(e),
                            onKeyDown: (ev: React.KeyboardEvent) => {
                              if (ev.key === "Enter" || ev.key === " ") {
                                ev.preventDefault();
                                onOpenEvent(e);
                              }
                            },
                          }
                        : {})}
                    >
                      {/* Time and day: the log reaches back a few days. */}
                      <span className="flex flex-col whitespace-nowrap font-ibm-plex text-xs tabular-nums text-gray-500 dark:text-white">
                        <span>{clock(e)}</span>
                        <span className="font-[family-name:var(--font-primary)] text-[11px] text-gray-400 dark:text-white/70">{day(e)}</span>
                      </span>
                      <span className="min-w-0">
                        <LiveBadge tone={t} icon={Icon} className={quiet ? "opacity-80" : undefined}>
                          {kindLabel(e.kind)}
                        </LiveBadge>
                      </span>
                      <span className="flex min-w-0 flex-col gap-0.5 font-[family-name:var(--font-primary)]">
                        {showStrategy ? (
                          <span className="flex min-w-0 items-baseline gap-1.5">
                            <span className="truncate text-sm font-medium text-[color:var(--brand-tropical-surf)]">{head}</span>
                            {tail ? <span className="shrink-0 truncate text-[11px] text-gray-500 dark:text-white">{tail}</span> : null}
                          </span>
                        ) : null}
                        <span className={cn("text-gray-500 dark:text-white", quiet ? "truncate text-xs" : "text-sm")}>{text(e)}</span>
                        {/* A problem's way forward: open the strategy to see and fix it. */}
                        {onOpenEvent && PROBLEM_KINDS.has(e.kind) ? (
                          <Button
                            type="button"
                            variant="outline-brand"
                            size="sm"
                            data-font="primary"
                            className="mt-1.5 h-7 w-fit gap-1 px-2.5 shadow-none"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              onOpenEvent(e);
                            }}
                          >
                            <span className="normal-case tracking-normal">Open strategy</span>
                          </Button>
                        ) : null}
                      </span>
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ol>
          ) : (
            <div className={cn(liveSmallTextClassName, "flex flex-col items-center gap-2 px-4 py-10 text-center")}>
              <Clock3 className="size-4 text-[color:var(--brand-tropical-surf)]" aria-hidden />
              {filter === "problems"
                ? "No problems."
                : strategy !== ALL_STRATEGIES && !deploymentId
                  ? "Nothing from this strategy yet. The first entry appears when its next candle closes."
                  : "The first entry appears when the next candle closes."}
            </div>
          )}
        </div>
    </section>
  );
}
